import { BusinessMemberDetails, MemberForm } from '@/types/businesses';
import { Dialog, DialogContent, Typography } from '@mui/material';
import { SVButton, SVForm } from '../core';
import BusinessMembersForm from './BusinessMembersForm';
import { MESSAGES } from '@/constants/messages';
import { useEditBusinessMembers } from '@/hooks/requests/useBusinesses';
import showToast from '@/utils/toast';
import { useFieldArray, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useState } from 'react';
import BusinessAddCustomerForm from './BusinessAddCustomerForm';
import { validateBusinessMemberForm } from '@/utils/business';

type EditBusinessMembersModalProps = {
  companyId?: string;
  advisors?: string[];
  members: BusinessMemberDetails[];
};

export default function EditBusinessMembersModal({
  companyId,
  advisors = [],
  members,
}: EditBusinessMembersModalProps) {
  const { uuid = '' } = useParams();
  const { editBusinessMembers, isEditBusinessMembersPending } =
    useEditBusinessMembers();

  const [openMembersForm, setOpenMembersForm] = useState(false);
  const [openAddCustomerModal, setOpenAddCustomerModal] = useState(false);

  const mappedDefaultValues = members.map(member => {
    const { consumer, ownershipPercentage, isControlPerson, title } = member;

    return {
      consumer: consumer.uuid,
      ownershipPercentage,
      title,
      isControlPerson,
    };
  });

  const formMethods = useForm<MemberForm>({
    defaultValues: {
      members: mappedDefaultValues,
    },
    mode: 'onBlur',
  });
  const membersField = formMethods.watch('members');

  const membersFieldArray = useFieldArray<MemberForm>({
    control: formMethods.control,
    name: 'members' as const,
  });

  if (!companyId) {
    return null;
  }

  async function handleEditBusinessMembers(data: MemberForm) {
    const isValid = validateBusinessMemberForm(data);
    if (!isValid) {
      return;
    }

    try {
      await editBusinessMembers({ ...data, uuid });
      showToast({
        type: 'success',
        data: {
          content: 'Business members have been successfully updated',
        },
      });
      setOpenMembersForm(false);
    } catch {
      showToast({
        type: 'error',
        data: {
          content: MESSAGES.genericError,
        },
      });
    }
  }

  return (
    <>
      <SVButton
        sx={{ mt: 2 }}
        variant="text"
        onClick={() => setOpenMembersForm(true)}>
        Edit Members
      </SVButton>
      <Dialog
        fullWidth
        maxWidth="lg"
        open={openMembersForm}
        onClose={() => setOpenMembersForm(false)}>
        <DialogContent>
          <Typography variant="h6" component="h2" mb={2}>
            Edit Members
          </Typography>
          <SVForm<MemberForm>
            methods={formMethods}
            onSubmit={handleEditBusinessMembers}>
            <BusinessMembersForm
              companyId={companyId}
              advisorIds={advisors}
              setOpenAddCustomerModal={setOpenAddCustomerModal}
              fieldArray={membersFieldArray}
              submitButton={
                <SVButton
                  isLoading={isEditBusinessMembersPending}
                  onClick={formMethods.handleSubmit(handleEditBusinessMembers)}>
                  Submit
                </SVButton>
              }
            />
          </SVForm>
        </DialogContent>
      </Dialog>
      <BusinessAddCustomerForm
        formMethods={formMethods}
        open={openAddCustomerModal}
        setOpen={setOpenAddCustomerModal}
        company={companyId}
        advisors={advisors}
        members={membersField}
        fieldArray={membersFieldArray}
      />
    </>
  );
}
